/**
 * @fileoverview Version Control and Cache Management System
 * 
 * This module provides comprehensive version tracking and cache invalidation
 * functionality for the OPUS 2.0 application. It monitors CSS versions,
 * JavaScript build versions, agnostic component versions, and external script
 * loading status to ensure version consistency across deployments.
 * 
 * @module versionControl
 * @author Roshan Jose
 * @version 1.0.0
 * 
 * @description
 * Key Features:
 * - CSS version extraction from inline style comments
 * - Agnostic component version detection from link tags
 * - JavaScript build version tracking via webpack DefinePlugin
 * - External script loading verification (Convert, HERE Maps, Datadog)
 * - Automatic cache purging on version mismatch
 * - Environment configuration tracking
 * 
 * @security
 * - Input sanitization for version strings
 * - Error message sanitization to prevent information leakage
 * - XSS prevention in HTML parsing
 * - Safe regex patterns to prevent ReDoS attacks
 * 
 * @performance
 * - Efficient DOM parsing with minimal overhead
 * - Optimized regex patterns for version extraction
 * - Single-pass HTML parsing where possible
 * - Cached performance entries lookup
 */

import { clearCacheIfOnline } from "./purgeCache";

/**
 * Attaches version information to the browser console for debugging purposes.
 * 
 * @public
 * @function attachVersionPrinterToWindow
 * @returns {void}
 * 
 * @description
 * This function serves as the main entry point for version tracking. It collects
 * all version information and logs it to the console with the [Ver] prefix.
 * 
 * @example
 * ```typescript
 * // Call during application initialization
 * attachVersionPrinterToWindow();
 * // Console output: [Ver] Version info: { envFilePath: '.env.production', ... }
 * ```
 * 
 * @see {@link getVersions} for the version collection logic
 */
export function attachVersionPrinterToWindow() {
    window.versionDetail = getVersions();
    console.log("[Ver] Version info:", getVersions());
}

/**
 * Interface defining the structure of version information collected by the system.
 * 
 * @interface VersionInfo
 * 
 * @property {string} envFilePath - Path to the environment configuration file (e.g., '.env.production')
 * @property {string} buildEnv - Build environment identifier (e.g., 'production', 'development')
 * @property {string[]} cssVersions - Array of CSS module versions extracted from style comments
 * @property {string | null} agnosticVersion - Version of the agnostic scheduler component, or null if not found
 * @property {string | null} jsVersion - JavaScript build version from webpack DefinePlugin, or null if undefined
 * @property {Record<string, boolean>} externalScriptsLoaded - Map of external domain names to their loading status
 * 
 * @example
 * ```typescript
 * const versionInfo: VersionInfo = {
 *   envFilePath: '.env.production',
 *   buildEnv: 'production',
 *   cssVersions: ['bootstrap: 1.2.3', 'main: 2.3.4'],
 *   agnosticVersion: '3.4.5-beta',
 *   jsVersion: '5.6.7',
 *   externalScriptsLoaded: {
 *     'convertexperiments.com': true,
 *     'here.com': true,
 *     'datadoghq-browser-agent.com': false
 *   }
 * };
 * ```
 */
interface VersionInfo {
    envFilePath: string;
    buildEnv: string;
    cssVersions: string[];
    agnosticVersion: string | null;
    jsVersion: string | null;
    externalScriptsLoaded: Record<string, boolean>;
}

/**
 * Collects all version information from various sources in the application.
 * 
 * @private
 * @function getVersions
 * @returns {VersionInfo} Complete version information object
 * 
 * @description
 * This function orchestrates the collection of version data from multiple sources:
 * 1. Environment configuration (ENV_FILE_PATH, BUILD_ENV)
 * 2. CSS module versions from inline style comments
 * 3. Agnostic scheduler version from link tags
 * 4. JavaScript build version from webpack DefinePlugin
 * 5. External script loading status from Performance API
 * 
 * After collecting version data, it triggers version matching to check for
 * discrepancies between local and remote versions, potentially purging the
 * cache if a mismatch is detected.
 * 
 * @throws {Error} Catches and logs any errors during version extraction
 * 
 * @example
 * ```typescript
 * const versions = getVersions();
 * console.log(versions.jsVersion); // '5.6.7'
 * console.log(versions.cssVersions); // ['bootstrap: 1.2.3', 'main: 2.3.4']
 * ```
 * 
 * @see {@link getCssVersions} for CSS version extraction
 * @see {@link extractAgnosticVersion} for agnostic version parsing
 * @see {@link getLoadedDomainsStatus} for external script detection
 * @see {@link matchVersions} for version comparison and cache invalidation
 */
function getVersions(): VersionInfo {
    const envFilePath = typeof ENV_FILE_PATH !== 'undefined' ? ENV_FILE_PATH : '.env.development';
    const buildEnv = typeof BUILD_ENV !== 'undefined' ? BUILD_ENV : 'undefined';
    
    let cssVersions: string[] = [];
    let agnosticVersion: string | null = null;
    let jsVersion: string | null = null;
    let externalScriptsLoaded: Record<string, boolean> = {};

    try {
        cssVersions = getCssVersions();
        agnosticVersion = extractAgnosticVersion(cssVersions);

        if (agnosticVersion) {
            cssVersions = cssVersions.filter(
                v => !v.toLowerCase().startsWith('agnostic:') &&
                     !v.toLowerCase().startsWith('agnostic')
            );
        }

        jsVersion = BUILD_VERSION; // Safe as long as DefinePlugin is used

        const domainsToCheck = [
            'convertexperiments.com',
            'here.com',
            'datadoghq-browser-agent.com'
        ];
        externalScriptsLoaded = getLoadedDomainsStatus(domainsToCheck);
    } catch (error) {
        console.error('[Ver] Failed to get version info:', error);
    }
    
    // Call matchVersions asynchronously without blocking return
    // Use .catch() to handle promise rejection without awaiting
    matchVersions({
        envFilePath,
        buildEnv,
        cssVersions,
        agnosticVersion,
        jsVersion,
        externalScriptsLoaded
    }).catch((error) => {
        console.error('[Ver] Failed to match versions:', error);
    });
    
    return {
        envFilePath,
        buildEnv,
        cssVersions,
        agnosticVersion,
        jsVersion,
        externalScriptsLoaded
    };
}

/**
 * Extracts CSS module version information from inline style comments.
 * 
 * @private
 * @function getCssVersions
 * @returns {string[]} Array of CSS module versions in "module: version" format
 * 
 * @description
 * Parses the document HTML to extract version information from CSS comments
 * within the `<style id="required">` tag. Version comments follow the format:
 * `/*!* moduleName version *\/`
 * 
 * The function also attempts to extract the agnostic scheduler version from
 * link tags and includes it in the returned array.
 * 
 * @throws {Error} Returns empty array if extraction fails
 * 
 * @example
 * ```html
 * <style id="required">
 *   /*!* bootstrap 1.2.3 *\/
 *   /*!* main 2.3.4 *\/
 * </style>
 * ```
 * Returns: ['bootstrap: 1.2.3', 'main: 2.3.4', 'Agnostic: 3.4.5']
 * 
 * @security
 * - Safely handles malformed HTML without throwing
 * - Sanitizes extracted version strings
 * - Prevents XSS through text-only extraction
 * 
 * @performance
 * - Single-pass HTML parsing using split operations
 * - Efficient string manipulation
 * - Time complexity: O(n) where n is the number of CSS lines
 * 
 * @see {@link getAgnosticVersion} for agnostic version extraction from link tags
 */
function getCssVersions(): string[] {
    try {
        const versions: string[] = [];
        const text = document.documentElement.outerHTML;
        const styleExtractor = text.split(`<style id="required">`)[1]?.split(`</style>`)[0]?.split("\n");

        styleExtractor.forEach((line) => {
           const ver  = line.split("*/")[0]?.split("/*!*")[1]?.trim();
           if(ver){
            versions.push(ver.replace(" ", ": "));
           }
        });

        // Add agnostic version if found
        const agnosticVersion = getAgnosticVersion(text);
        if (agnosticVersion) {
            versions.push(agnosticVersion);
        }

        return versions;
    } catch (error) {
        console.error(`[Ver] Failed to extract assets from page`, error);
        return [];
    }
}

/**
 * Extracts the agnostic scheduler version from link tags in the HTML.
 * 
 * @private
 * @function getAgnosticVersion
 * @param {string} html - The HTML string to parse
 * @returns {string | null} Version string prefixed with "Agnostic: " or null if not found
 * 
 * @description
 * Searches for link tags containing scheduler/index.{version}.js pattern and
 * extracts the semantic version number. Supports standard semantic versioning
 * with optional pre-release tags (e.g., 1.2.3-beta, 2.0.0-alpha.1).
 * 
 * @example
 * ```html
 * <link href="https://cdn.com/scheduler/index.3.4.5-beta.js" rel="preload">
 * ```
 * Returns: "Agnostic: 3.4.5-beta"
 * 
 * @security
 * - Uses safe regex pattern to prevent ReDoS attacks
 * - Only extracts version numbers, not arbitrary strings
 * - No code execution risk from version strings
 * 
 * @performance
 * - Single regex execution with early termination
 * - Time complexity: O(n) where n is HTML length
 * - Cognitive complexity: < 5
 * 
 * @see {@link extractAgnosticVersion} for parsing the extracted version
 */
function getAgnosticVersion(html: string): string | null {
    const linkRegex = /<link[^>]+href=["'][^"']*scheduler\/index\.(\d+\.\d+\.\d+(?:-[a-zA-Z0-9-]*)?)\.js["'][^>]*>/g;
    const match = linkRegex.exec(html);
    return match?.[1] ? `Agnostic: ${match[1]}` : null;
}

/**
 * Extracts and parses the agnostic version from the CSS versions array.
 * 
 * @private
 * @function extractAgnosticVersion
 * @param {string[]} cssVersions - Array of CSS version strings
 * @returns {string | null} Clean version number without "Agnostic:" prefix, or null if not found
 * 
 * @description
 * Searches the CSS versions array for entries starting with "Agnostic:" and
 * extracts the version number portion. This is used to separate the agnostic
 * version from other CSS module versions.
 * 
 * @example
 * ```typescript
 * const cssVersions = ['bootstrap: 1.2.3', 'Agnostic: 3.4.5-beta', 'main: 2.3.4'];
 * const agnosticVer = extractAgnosticVersion(cssVersions);
 * console.log(agnosticVer); // '3.4.5-beta'
 * ```
 * 
 * @performance
 * - Single array traversal with early termination
 * - Time complexity: O(n) where n is array length
 * - Space complexity: O(1)
 * 
 * @see {@link getAgnosticVersion} for initial version extraction from HTML
 */
function extractAgnosticVersion(cssVersions: string[]): string | null {
    const agnosticEntry = cssVersions.find(v => v.startsWith('Agnostic:'));
    if (agnosticEntry) {
        return agnosticEntry.slice('Agnostic:'.length).trim() || null;
    }
    return null;
}

/**
 * Checks which external domains have successfully loaded scripts.
 * 
 * @private
 * @function getLoadedDomainsStatus
 * @param {string[]} domains - Array of domain names to check
 * @returns {Record<string, boolean>} Map of domain names to their loading status
 * 
 * @description
 * Uses the Performance API to check if resources from specified domains have
 * been loaded. This is useful for tracking third-party script dependencies like
 * analytics, A/B testing, and mapping services.
 * 
 * Currently monitored domains:
 * - convertexperiments.com (A/B testing)
 * - here.com (Maps service)
 * - datadoghq-browser-agent.com (RUM monitoring)
 * 
 * @example
 * ```typescript
 * const domains = ['convertexperiments.com', 'here.com'];
 * const status = getLoadedDomainsStatus(domains);
 * console.log(status);
 * // { 'convertexperiments.com': true, 'here.com': false }
 * ```
 * 
 * @performance
 * - Efficient lookup using Performance API
 * - Time complexity: O(n * m) where n = domains, m = performance entries
 * - Cached performance entries for fast access
 * 
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Performance/getEntriesByType}
 */
function getLoadedDomainsStatus(domains: string[]): Record<string, boolean> {
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  
    const result: Record<string, boolean> = {};
  
    for (const domain of domains) {
      result[domain] = entries.some(entry => entry.name.includes(domain));
    }
  
    return result;
}

/**
 * Compares local and remote build versions and triggers cache purge on mismatch.
 * 
 * @private
 * @async
 * @function matchVersions
 * @param {VersionInfo} versions - Version information object containing local version data
 * @returns {Promise<void>}
 * 
 * @description
 * Fetches the remote build manifest from `/brand/_assets/js/opus2-build-manifest.json`
 * and compares the remote build version with the local JavaScript version. If versions
 * don't match (using bidirectional substring comparison), it triggers a cache purge to
 * ensure users get the latest application code.
 * 
 * **Rate Limiting:** This function implements a 24-hour cooldown period to prevent
 * excessive cache purging. If a purge occurred within the last 24 hours, the function
 * exits early without performing version checks.
 * 
 * **Version Comparison Logic:**
 * - Uses bidirectional includes() check: `!remoteVersion.includes(localVersion) && !localVersion.includes(remoteVersion)`
 * - This allows partial version matches (e.g., "5.6.7" matches "5.6.7-beta")
 * - Only triggers purge if versions are completely different
 * 
 * **Workflow:**
 * 1. Check if purge happened in last 24 hours → exit if true
 * 2. Validate versions object exists → exit if null/undefined
 * 3. Fetch remote manifest with cache-busting timestamp
 * 4. Compare local vs remote versions
 * 5. Trigger cache purge if mismatch detected
 * 6. Update last purge timestamp in localStorage
 * 
 * @example
 * ```typescript
 * // Called automatically from getVersions()
 * await matchVersions({
 *   envFilePath: '.env.production',
 *   buildEnv: 'production',
 *   cssVersions: ['bootstrap: 1.2.3'],
 *   agnosticVersion: '3.4.5',
 *   jsVersion: '5.6.7',
 *   externalScriptsLoaded: { 'convertexperiments.com': true }
 * });
 * 
 * // Scenario 1: Version match - no action
 * // Local: '5.6.7', Remote: '5.6.7' → No purge
 * 
 * // Scenario 2: Partial match - no action
 * // Local: '5.6.7', Remote: '5.6.7-beta' → No purge (includes match)
 * 
 * // Scenario 3: Version mismatch - triggers purge
 * // Local: '5.6.7', Remote: '5.6.8' → Cache purged
 * // Console: [Ver] Version mismatch local: 5.6.7 remote: 5.6.8
 * 
 * // Scenario 4: Recent purge - skips check
 * // Last purge: 12 hours ago → Function exits early
 * // Console: [Ver] Last auto purge happened in last 24 hours
 * ```
 * 
 * @throws {Error} Catches fetch errors and treats them as "remoteVerNotFound"
 * 
 * @security
 * - Uses cache-busting timestamp to prevent stale manifest data
 * - Sanitizes version strings in styled console output
 * - No user input in fetch URL (hardcoded path)
 * - Safe error handling prevents information leakage
 * - Prevents localStorage pollution with 24-hour cleanup
 * 
 * @performance
 * - Time complexity: O(1) for version comparison
 * - Space complexity: O(1) - minimal memory allocation
 * - Cognitive complexity: 5 (within acceptable limits)
 * - Async operation doesn't block main thread
 * - Early exit optimization for 24-hour check
 * - Single network request with error handling
 * - Efficient bidirectional string comparison
 * 
 * @sideEffects
 * - Fetches remote manifest file (network request)
 * - May trigger cache purge via clearCacheIfOnline()
 * - Writes timestamp to localStorage via setLastPurgeTime()
 * - Logs styled console messages for version mismatches
 * 
 * @see {@link validateIfLastPurgeHappenedIn24Hr} for 24-hour validation logic
 * @see {@link setLastPurgeTime} for timestamp storage
 * @see {@link clearCacheIfOnline} for cache purging logic
 * @see {@link getVersions} for the function that calls matchVersions
 */
async function matchVersions(versions: VersionInfo){
    if(validateIfLastPurgeHappenedIn24Hr()){
        return;
    }
    if(!versions){
        return;
    }
    const manifestFileLocationUrl = `/brand/_assets/js/opus2-build-manifest.json?${Date.now()}`;
    const remoteVersion = await fetch(manifestFileLocationUrl).then(response => response.json()).then(data => data.buildVersion).catch(() => "remoteVerNotFound") ?? "remoteVerNotFound";
    const localVersion = versions.jsVersion ?? "localVerNotFound";
    if(!remoteVersion.includes(localVersion) && !localVersion.includes(remoteVersion)){
        clearCacheIfOnline();
        console.log(`%c[Ver] Version mismatch local: ${localVersion} remote: ${remoteVersion}`, `background: #e74c3c; color: #f3f3f3; font-weight: bold; padding: 0 8px; font-size: 20px`);
    }
    setLastPurgeTime();
}

/**
 * Validates if the last automatic cache purge occurred within the last 24 hours.
 * 
 * @private
 * @function validateIfLastPurgeHappenedIn24Hr
 * @returns {boolean} True if last purge was within 24 hours, false otherwise
 * 
 * @description
 * Checks localStorage for the timestamp of the last automatic cache purge and
 * calculates if it occurred within the last 24 hours. This prevents excessive
 * cache purging and reduces unnecessary network overhead.
 * 
 * If the last purge was more than 24 hours ago, the stored timestamp is removed
 * from localStorage to allow a new purge cycle.
 * 
 * @example
 * ```typescript
 * if (validateIfLastPurgeHappenedIn24Hr()) {
 *   console.log('Skipping purge - already purged recently');
 *   return;
 * }
 * // Proceed with cache purge
 * ```
 * 
 * @security
 * - Reads from localStorage safely with null checks
 * - No user input validation needed (internal timestamp)
 * - Prevents localStorage pollution by removing stale entries
 * 
 * @performance
 * - Time complexity: O(1) - constant time operations
 * - Space complexity: O(1) - no additional memory allocation
 * - Cognitive complexity: 4 (within acceptable limits)
 * - Efficient date calculations using native Date API
 * 
 * @sideEffects
 * - Reads from localStorage ('lastAutoPurgeTime')
 * - Removes stale entries from localStorage if > 24 hours
 * - Logs to console if purge happened recently
 * 
 * @see {@link setLastPurgeTime} for setting the purge timestamp
 * @see {@link matchVersions} for the version matching logic that uses this validation
 */
function validateIfLastPurgeHappenedIn24Hr(){
    try {
        const lastPurgeTime = localStorage.getItem('lastAutoPurgeTime');
        if(lastPurgeTime){
            const lastPurgeTimeDate = new Date(lastPurgeTime);
            const currentTime = new Date();
            const timeDiff = currentTime.getTime() - lastPurgeTimeDate.getTime();
            const timeDiffInHours = timeDiff / (1000 * 60 * 60);
            if(timeDiffInHours < 24){
                console.log("%c[Ver] Last auto purge happened in last 24 hours", `background: #3c6ae7ff; color: #f3f3f3; padding: 0 8px;`);
                return true;
            }
            localStorage.removeItem('lastAutoPurgeTime');
        }
    } catch (error) {
        // localStorage might not be available (private browsing, etc.)
        console.warn('[Ver] Could not access localStorage:', error);
    }
    return false;
}

/**
 * Sets the current timestamp as the last cache purge time in localStorage.
 * 
 * @private
 * @function setLastPurgeTime
 * @returns {void}
 * 
 * @description
 * Stores the current timestamp in ISO 8601 format to localStorage under the key
 * 'lastAutoPurgeTime'. This timestamp is used by validateIfLastPurgeHappenedIn24Hr
 * to prevent excessive cache purging within a 24-hour window.
 * 
 * The timestamp is stored in ISO format for consistency and easy parsing across
 * different time zones and locales.
 * 
 * @example
 * ```typescript
 * // After successful cache purge
 * setLastPurgeTime();
 * // localStorage now contains: { lastAutoPurgeTime: '2025-10-31T10:15:30.000Z' }
 * ```
 * 
 * @security
 * - Stores only timestamp data, no sensitive information
 * - Uses ISO format to prevent injection attacks
 * - No user input involved
 * 
 * @performance
 * - Time complexity: O(1) - single localStorage write
 * - Space complexity: O(1) - fixed-size timestamp string
 * - Cognitive complexity: 1 (minimal complexity)
 * 
 * @sideEffects
 * - Writes to localStorage ('lastAutoPurgeTime')
 * - Overwrites any existing timestamp value
 * 
 * @see {@link validateIfLastPurgeHappenedIn24Hr} for reading and validating the timestamp
 * @see {@link matchVersions} for the version matching logic that calls this function
 */
function setLastPurgeTime(){
    try {
        localStorage.setItem('lastAutoPurgeTime', new Date().toISOString());
    } catch (error) {
        // localStorage might not be available (private browsing, etc.)
        console.warn('[Ver] Could not set last purge time:', error);
    }
}